import React from "react";
import { AlertTriangle } from "lucide-react";
import { LegalLayout, Section, Bullets } from "@/components/legal/LegalLayout";
import { SUPPORT_EMAIL } from "@/constants/legal";

export default function Disclaimer() {
  return (
    <LegalLayout
      slug="disclaimer"
      title="Risk Assessment Disclaimer"
      subtitle="Please read this disclaimer carefully. It explains the important limitations of HostGuard’s risk scores and AI summaries."
    >
      <div
        className="flex gap-3 rounded-xl border border-[hsl(var(--risk-high))]/30 bg-[hsl(var(--risk-high))]/5 p-5"
        data-testid="disclaimer-callout"
      >
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--risk-high))]" />
        <p className="text-sm font-medium leading-relaxed text-foreground">
          HostGuard provides informational risk guidance only and does not guarantee booking outcomes.
          Users remain solely responsible for booking decisions.
        </p>
      </div>

      <Section title="1. Informational purposes only">
        <p>Risk scores, sub-scores, contributing factors and AI-generated summaries produced by HostGuard are <strong>informational only</strong>. They are a guidance tool to help you think through potential booking risk — they are not professional, legal, financial or security advice.</p>
      </Section>

      <Section title="2. Guidance, not a decision">
        <p>HostGuard does not make booking decisions for you and does not replace your own judgement. You remain solely responsible for deciding whether to accept, decline or proceed with any booking, and for how you communicate with guests.</p>
      </Section>

      <Section title="3. No guarantee of guest behaviour">
        <p>A risk score reflects only the booking characteristics you enter. HostGuard does <strong>not</strong> verify guest identity and <strong>cannot</strong> predict or guarantee how any guest will actually behave. A “low risk” result is not a guarantee of a problem-free stay, and a “high risk” result does not mean a guest will cause harm.</p>
      </Section>

      <Section title="4. No liability for losses">
        <p>To the maximum extent permitted by law, HostGuard is not liable for any property damage, theft, nuisance, fraud, chargebacks, cancellations, lost revenue or other financial losses arising from a booking, regardless of the risk score or summary provided.</p>
      </Section>

      <Section title="5. AI summaries may be inaccurate">
        <p>AI summaries are generated automatically by third-party AI models. They may contain inaccuracies, omissions or generalisations and should be read with caution. AI output is intended to summarise the structured inputs you provided and must not be treated as a statement of fact about any individual.</p>
      </Section>

      <Section title="6. Do not rely on this as your sole basis">
        <p>Assessments should <strong>not</strong> be the sole basis for accepting or declining any guest. You should consider platform policies, your own house rules, direct communication with the guest, and your independent judgement.</p>
      </Section>

      <Section title="7. Lawful, non-discriminatory use">
        <p>You must use HostGuard lawfully and must not use risk scores to discriminate against any person on the basis of a protected attribute. You are responsible for complying with all applicable anti-discrimination laws, privacy laws and platform policies.</p>
      </Section>

      <Section title="8. Acknowledgement">
        <p>By creating an account and using HostGuard, you acknowledge that you have read and understood this disclaimer and accept these limitations. This disclaimer is also displayed on every assessment result. Questions? Contact <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-primary hover:underline">{SUPPORT_EMAIL}</a>.</p>
      </Section>
    </LegalLayout>
  );
}
